import React, { useEffect, useMemo, useState } from "react";
import { Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import { CircularProgress } from '@mui/material';
import FeatherIcon from "feather-icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TableComponent from "../../components/table/list.jsx";
import { CustomTooltip, LinearProgressWithLabel } from "../../consts.js";
import Utils from "../../utils";
import ChangeArchiveConfirm from "./change-archive-confirm.jsx";
import EditAgentNameComponent from "./edit-agent-name.jsx";
import { format } from "date-fns";
import { DATE_FORMAT } from "../../helpers/constant.js";
import { useThemeMode } from "../../components/theme/index.jsx";
import { roundNumberHelper } from "../../helpers/index.js";
import { getConversationListAPI } from "../../services/conversation.js";

function CampaignConversations({ updated }) {
    const [openEditAgentName, setOpenEditAgentName] = useState(false);
    const [editConversation, setEditConversation] = useState(null);
    const [openArchiveConfirm, setOpenArchiveConfirm] = useState(false);

    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { colours, FONT } = useThemeMode();
    const [campaignConversationsId] = useState(params.get("id") || "");
    const [data, setData] = useState([]);
    const [spinner, setSpinner] = useState(false);
    const [processedConversations, setProcessedConversations] = useState(0);
    const [totalConversations, setTotalConversations] = useState(0);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        rowsPerPage: 10,
        nextPage: null,
    });

    async function getCampaignData(rowsPerPage = pageInfo.rowsPerPage, nextPage = pageInfo.nextPage) {
        Utils.sendProgressBar(true);
        setSpinner(true);

        try {
            // api function
            const { items, lastKey, totalConversations, totalProcessed } = await getConversationListAPI({
                campaignId: campaignConversationsId,
                archive: false,
                limit: rowsPerPage,
                lastKey: nextPage
            });
            let newData = [...items];
            if (nextPage) {
                newData = [...data, ...items];
            }
            setData([...newData]);
            setPageInfo((prev) => ({ ...prev, nextPage: lastKey || null }));
            setTotalConversations(totalConversations);
            setProcessedConversations(totalProcessed);
            setSpinner(false);
        } catch (error) {
            setSpinner(false);
            console.error("Error fetching campaign data:", error);
        }
    }

    useEffect(() => {
        getCampaignData(); // Your async data fetching function
        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, []);

    //  used when a conversation uploaded
    useEffect(() => {
        if (updated !== 0) {
            setPageInfo({...pageInfo, nextPage: null, currentPage: 0 });
            getCampaignData(pageInfo.rowsPerPage, null);
        }
    }, [updated]);

    /**
     * This method used to reload the page after api success
     */
    const handleArchiveSuccess = () => {
        setOpenArchiveConfirm(false);
        setData([]);
        setPageInfo({...pageInfo, nextPage: null, currentPage: 0 });
        getCampaignData(pageInfo.rowsPerPage, null);
    }

    // define table headers
    const columns = useMemo(() => ([
        {
            label: (<Checkbox
                checked={
                    data.length === 0
                        ? false
                        : data
                            .map((q) => q.selected)
                            .filter((q) => q !== true).length === 0 || false
                }
                onClick={(e) => {
                    let d = Object.assign([], data);
                    d.forEach((q) => (q.selected = e.target.checked));
                    setData(d);
                }}
                inputProps={{ 'aria-label': 'Select all rows' }}
            />),
            key: "selected",
            sortable: false,
            renderItem: (row) => (<Checkbox
                checked={row.selected || false}
                onClick={(e) => {
                    e.stopPropagation();
                    const d = Object.assign([], data);
                    const index = d.findIndex((item) => item.id === row.id);
                    d[index].selected = e.target.checked;
                    setData(d);
                }}
                inputProps={{ 'aria-label': `Select ${row.file}` }}
            />)
        },
        {
            label: "File",
            key: "file",
            sortable: false,
            renderItem: (row) => (<Stack direction="row" alignItems="center" gap={2}>
                <FeatherIcon icon="phone-incoming" size={20} style={{ flexShrink: 0 }} />
                {row.file || "-"}
            </Stack>)
        },
        {
            label: "Agent",
            key: "agent",
            sortable: false,
            style: { minWidth: 150 },
            renderItem: (row) => (<Stack direction="row" alignItems="center" gap={1} >
                {row.agent || "-"}
                <CustomTooltip title="Edit">
                    <IconButton
                        color="inherit"
                        aria-label="Edit"
                        onClick={async (e) => {
                            e.stopPropagation();
                            setEditConversation(row);
                            setOpenEditAgentName(true);
                        }}
                    >
                        <FeatherIcon icon="edit-2" size={18} />
                    </IconButton>
                </CustomTooltip>
            </Stack>)
        },
        {
            label: "Call Date",
            key: "createdAt",
            sortable: true,
            renderItem: (row) => (<>
                {format(new Date(row.createdAt), DATE_FORMAT) || "-"}
            </>)
        },
        {
            label: "Avg. Call Duration",
            key: "duration",
            sortable: false,
            renderItem: (row) => (row.formatted_duration ? row.formatted_duration : "-")
        },
        {
            label: "Avg. Vocalysd Score",
            key: "symblstatus",
            sortable: false,
            renderItem: (row) => (<>
                {row.symblstatus === "completed" ? (
                    <LinearProgressWithLabel variant="determinate" value={roundNumberHelper(row.VS)} />
                ) : row.symblstatus === "failed" || row.symblstatus === "DEEPGRAM_FAILED" ? (
                    <>
                        <CustomTooltip title={row.message || "Invalid or corrupt audio file"}>
                            <Typography component={"span"}>
                                <FeatherIcon
                                    aria-label="Invalid or corrupt audio file"
                                    icon="alert-triangle"
                                    size="20"
                                    style={{
                                        color: colours.orange100,
                                        flexShrink: 0
                                    }}
                                />
                            </Typography>
                        </CustomTooltip>
                    </>
                ) : (<Typography variant="inherit" display='flex' alignItems='center' gap={1} fontSize={FONT.SIZE_12}><CircularProgress aria-label="Loading status" size={14} color="info" /> Analysis in progress</Typography>)}
            </>)
        },
        {
            label: "# of Alert Hits",
            key: "alertcount",
            sortable: false,
            renderItem: (row) => (<>
                {row.alertcount > 0 ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ fontWeight: 500, color: colours.orange100 }}>
                        {row.alertcount}
                        <FeatherIcon
                            aria-label="Alert status"
                            icon="alert-circle"
                            size="20"
                            style={{ flexShrink: 0 }}
                        />
                    </Stack>
                ) : "-"}
            </>)
        }
    ]), [data, colours, FONT]);

    const handleReload = () => {
        setPageInfo({  ...pageInfo, nextPage: null, currentPage: 0 });
        setData([]);
        getCampaignData(pageInfo.rowsPerPage, null); 
        setTotalConversations(0);
        setProcessedConversations(0);
    }

    const handlePageChange = (_pageInfo) => {
        let nextPage = pageInfo.nextPage;
        if(_pageInfo.REFRESH_DATA) {
            nextPage = null
        }
        setPageInfo({ 
            ...pageInfo,
            nextPage,
            currentPage: _pageInfo.currentPage,
            rowsPerPage: _pageInfo.rowsPerPage
        });
        if(nextPage || _pageInfo.REFRESH_DATA) {
            getCampaignData(_pageInfo.rowsPerPage, nextPage); 
        }
    }


    const headerActionButtons = () => {
        return (
            <Grid
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="end"
                flexGrow={1}
                gap={2}
            >
                {data.filter((z) => z.selected === true).length > 0 ? (
                    <Typography component="div"
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                        }}
                    >
                        <Button
                            onClick={() => setOpenArchiveConfirm(true)}
                            variant="outlined"
                            startIcon={<FeatherIcon icon="archive" size={20} />}
                            endIcon={<Chip
                                size="small"
                                label={data.filter((z) => z.selected === true).length}
                                sx={{ px: .25 }}
                            />}
                            sx={{
                                '.MuiButton-endIcon .MuiChip-root': {
                                    fontSize: FONT.SIZE_12,
                                    fontWeight: 500
                                }
                            }}
                        >
                            Archive
                        </Button>
                        <Divider orientation="vertical" sx={{ borderColor: "grey.500" }} variant="middle" flexItem />
                    </Typography>
                ) : null}

                <Typography component="div"
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        fontSize: FONT.SIZE_14,
                        gap: 1,
                    }}
                >
                    {processedConversations} of{" "} {totalConversations} Files Processed
                    <CustomTooltip title="Refresh">
                        <IconButton aria-label="Refresh" onClick={handleReload} color="primary">
                            <FeatherIcon
                                icon="refresh-cw"
                                size={20}
                            />
                        </IconButton>
                    </CustomTooltip>
                </Typography>
            </Grid>
        )
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    serverPaginate={true}
                    currentPage={pageInfo.currentPage}
                    hasNextPage={pageInfo.nextPage !== null}
                    totalCount={totalConversations}
                    searchKeys={["file", "agent"]}
                    noDataMessage={"No conversation found."}
                    defaultOptions={{ sortKey: 'createdAt', sortOrder: 'desc', rowPerPage: pageInfo.rowsPerPage }}
                    onPageChange={handlePageChange}
                    rowClick={(item) => {
                        if (item.symblstatus === "completed") {
                            navigate("/conversation?id=" + item.id);
                        }
                    }}
                />

                {/* change to archive confirm popup */}
                <ChangeArchiveConfirm
                    setArchiveState={true} // true to archive the conversation 
                    open={openArchiveConfirm}
                    data={data}
                    onClose={() => setOpenArchiveConfirm(false)}
                    onSuccess={handleArchiveSuccess} />
                {/* edit agent popup */}
                <EditAgentNameComponent
                    open={openEditAgentName}
                    data={editConversation}
                    onClose={() => {
                        setOpenEditAgentName(false)
                        setEditConversation(null);
                    }}
                    onSuccess={() => {
                        setOpenEditAgentName(false);
                        setEditConversation(null);
                        getCampaignData();
                    }}
                />
            </Grid>
        </Grid>
    );
}

export default CampaignConversations;
